import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { showNotification } from '@mantine/notifications'
import { useEffect, useRef } from 'react'
import type { OrpcClient } from '@/orpc/client'
import { logError } from '@/utils/errorUtils'
import { useOrpc } from './useClient'

// Types are read off the oRPC client so they follow the contract (never import middleware internals).
export type DuplicateReviewStatusTab = Parameters<OrpcClient['duplicateReviewList']>[0]['status']
export type DuplicateReviewSummary = Awaited<
  ReturnType<OrpcClient['duplicateReviewList']>
>['data'][number]
export type DuplicateReviewComparison = Awaited<ReturnType<OrpcClient['duplicateReviewGet']>>
export type MergeStatus = Awaited<ReturnType<OrpcClient['patientMergeGet']>>
export type PatientMergeInput = Parameters<OrpcClient['patientMerge']>[0]
export type DuplicateReviewDecision = Parameters<OrpcClient['duplicateReviewDecide']>[0]['decision']

export const duplicateReviewKeys = {
  all: ['duplicate-reviews'] as const,
  list: (status: DuplicateReviewStatusTab) => [...duplicateReviewKeys.all, 'list', status] as const,
  counts: () => [...duplicateReviewKeys.all, 'counts'] as const,
  detail: (reviewId: string, keepPatientId?: string) =>
    [...duplicateReviewKeys.all, 'detail', reviewId, keepPatientId ?? null] as const,
  forPatient: (patientId: string) =>
    [...duplicateReviewKeys.all, 'for-patient', patientId] as const,
}

export const mergeKeys = {
  detail: (mergeId: string) => ['patient-merge', mergeId] as const,
  redirect: (patientId: string) => ['patient-merge', 'redirect', patientId] as const,
  merged: () => ['merged-patients'] as const,
  settings: () => ['patient-merge-settings'] as const,
}

const REVIEW_PAGE_SIZE = 20
const MERGED_PAGE_SIZE = 20
const MERGE_POLL_MS = 2000
/** While interrupted: slow, so a run the server wrongly judged dead still shows its result (R36). */
const RESUMABLE_POLL_MS = 15_000
const MERGED_LIST_POLL_MS = 5000

function notifyFailure(action: string, error: unknown, message: string) {
  logError('patient', action, error)
  showNotification({ color: 'red', message })
}

/** Awaiting / Review-later counts and the patients named by an open review (Patients page). */
export function useDuplicateReviewCounts() {
  const orpc = useOrpc()
  return useQuery(
    orpc.duplicateReviewCounts.queryOptions({
      queryKey: duplicateReviewKeys.counts(),
      staleTime: 30 * 1000,
    }),
  )
}

/** One queue tab, twenty at a time. */
export function useDuplicateReviewList(status: DuplicateReviewStatusTab) {
  const orpc = useOrpc()
  return useInfiniteQuery(
    orpc.duplicateReviewList.infiniteOptions({
      input: (offset: number) => ({
        status,
        page: { offset, count: REVIEW_PAGE_SIZE, limit: Number.MAX_SAFE_INTEGER },
      }),
      queryKey: duplicateReviewKeys.list(status),
      initialPageParam: 0,
      getNextPageParam: (last) => {
        const next = last.page.offset + last.page.count
        return next < last.page.total ? next : undefined
      },
    }),
  )
}

/** Opens (or finds) the review for two chosen patients; the caller navigates to it. */
export function useOpenDuplicateReview() {
  const orpc = useOrpc()
  return useMutation(
    orpc.duplicateReviewOpen.mutationOptions({
      onError: (error) =>
        notifyFailure(
          'Open duplicate review',
          error,
          // A refusal says why (e.g. the records share no organization); anything else is retryable.
          refusalMessage(error) ?? 'Could not open the review. Please try again.',
        ),
    }),
  )
}

/**
 * The side-by-side comparison; conflicts are computed for keeping `keepPatientId`. Switching the
 * kept record keeps the previous comparison on screen (`isPlaceholderData`) until the new one lands.
 */
export function useDuplicateReview(reviewId: string, keepPatientId?: string) {
  const orpc = useOrpc()
  return useQuery(
    orpc.duplicateReviewGet.queryOptions({
      input: { reviewId, keepPatientId },
      queryKey: duplicateReviewKeys.detail(reviewId, keepPatientId),
      enabled: !!reviewId,
      placeholderData: keepPreviousData,
    }),
  )
}

/** Not a duplicate / Review later / Reopen. */
export function useDecideDuplicateReview(options?: { onSuccess?: () => void }) {
  const orpc = useOrpc()
  const queryClient = useQueryClient()
  return useMutation(
    orpc.duplicateReviewDecide.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: duplicateReviewKeys.all })
        options?.onSuccess?.()
      },
      onError: (error) =>
        notifyFailure(
          'Decide duplicate review',
          error,
          'Could not save the decision. Please try again.',
        ),
    }),
  )
}

/** The open review that names this patient (chart banner and Patients row), if any. */
export function useDuplicateReviewForPatient(patientId: string) {
  const orpc = useOrpc()
  return useQuery(
    orpc.duplicateReviewForPatient.queryOptions({
      input: { patientId },
      queryKey: duplicateReviewKeys.forPatient(patientId),
      enabled: !!patientId,
    }),
  )
}

/** Fetches the open review for one patient on demand (the row "Review" button). */
export function useFindDuplicateReviewForPatient() {
  const orpc = useOrpc()
  const queryClient = useQueryClient()
  return async (patientId: string) => {
    const result = await queryClient.fetchQuery(
      orpc.duplicateReviewForPatient.queryOptions({
        input: { patientId },
        queryKey: duplicateReviewKeys.forPatient(patientId),
        staleTime: 0,
      }),
    )
    return result.reviewId
  }
}

/**
 * The server's own message for a refused request (4xx — reason rules, blockers, a merge already
 * running), else undefined. Read duck-typed: `instanceof ORPCError` does not survive bundle
 * boundaries.
 */
function refusalMessage(error: unknown): string | undefined {
  const { status, message } = (error ?? {}) as { status?: unknown; message?: unknown }
  const refused = typeof status === 'number' && status >= 400 && status < 500
  return refused && typeof message === 'string' && message ? message : undefined
}

/**
 * Starts a merge; the page then follows `usePatientMerge(mergeId)`. `onSuccess` is hook-level, so
 * it still runs if the component that called `mutate` (the confirmation modal) has unmounted.
 */
export function useMergePatients(options?: { onSuccess?: (result: { mergeId: string }) => void }) {
  const orpc = useOrpc()
  const queryClient = useQueryClient()
  return useMutation(
    orpc.patientMerge.mutationOptions({
      onSuccess: async (result) => {
        options?.onSuccess?.(result)
        await queryClient.invalidateQueries({ queryKey: duplicateReviewKeys.all })
      },
      onError: (error) =>
        notifyFailure(
          'Merge patients',
          error,
          refusalMessage(error) ?? 'Could not start the merge. Please try again.',
        ),
    }),
  )
}

/**
 * How often the merge progress view polls: every two seconds while the run is unfinished. An
 * interrupted merge (`resumable`) waits for Resume, polled only slowly in case the run is alive
 * after all (R36).
 */
export function mergePollMs(merge: MergeStatus | undefined): number | false {
  if (!merge) return false
  if (merge.resumable) return RESUMABLE_POLL_MS
  return merge.runStatus === 'in-progress' ? MERGE_POLL_MS : false
}

/** Merge progress, polled per `mergePollMs`. */
export function usePatientMerge(mergeId: string | undefined) {
  const orpc = useOrpc()
  const query = useQuery(
    orpc.patientMergeGet.queryOptions({
      input: { mergeId: mergeId ?? '' },
      queryKey: mergeKeys.detail(mergeId ?? ''),
      enabled: !!mergeId,
      refetchInterval: (query) => mergePollMs(query.state.data),
    }),
  )
  useRefreshReviewsWhenSettled(query.data?.runStatus === 'in-progress')
  return query
}

/**
 * The run closes (or, on Restore, reopens) duplicate reviews only after the request that queued it
 * has answered, so the review queue, its counts and the row pills are refreshed again once a polled
 * merge or restore stops running — not only when it was queued.
 */
function useRefreshReviewsWhenSettled(running: boolean) {
  const queryClient = useQueryClient()
  const wasRunning = useRef(running)
  useEffect(() => {
    if (wasRunning.current && !running) {
      void queryClient.invalidateQueries({ queryKey: duplicateReviewKeys.all })
    }
    wasRunning.current = running
  }, [running, queryClient])
}

/** Finishes an interrupted merge. */
export function useResumeMerge() {
  const orpc = useOrpc()
  const queryClient = useQueryClient()
  return useMutation(
    orpc.patientMergeResume.mutationOptions({
      // The action returns the refreshed merge; write it straight into the polled query.
      onSuccess: (merge) => queryClient.setQueryData(mergeKeys.detail(merge.mergeId), merge),
      onError: (error) =>
        notifyFailure('Resume merge', error, 'Could not resume the merge. Please try again.'),
    }),
  )
}

/** Where an old link to a merged (possibly deleted) patient should lead. */
export function usePatientMergeRedirect(patientId: string, enabled: boolean) {
  const orpc = useOrpc()
  return useQuery(
    orpc.patientMergeRedirect.queryOptions({
      input: { patientId },
      queryKey: mergeKeys.redirect(patientId),
      enabled: enabled && !!patientId,
      retry: false,
    }),
  )
}

/** Super admin: every merge, newest first. */
/**
 * The Merged patients list polls while a row's merge or restore run is still going, so "Restoring…"
 * settles to "Restored" without a reload. A failed run waits for the super admin, so it stops.
 */
export function mergedListPollMs(
  data: { pages: Array<{ data: MergeStatus[] }> } | undefined,
): number | false {
  const running = (data?.pages ?? []).some((page) =>
    page.data.some(
      (m) =>
        (m.state === 'merging' || m.state === 'restoring') &&
        m.runStatus === 'in-progress' &&
        !m.resumable,
    ),
  )
  return running ? MERGED_LIST_POLL_MS : false
}

export function useMergedPatients() {
  const orpc = useOrpc()
  const query = useInfiniteQuery(
    orpc.mergedPatientList.infiniteOptions({
      input: (offset: number) => ({
        page: { offset, count: MERGED_PAGE_SIZE, limit: Number.MAX_SAFE_INTEGER },
      }),
      queryKey: mergeKeys.merged(),
      initialPageParam: 0,
      getNextPageParam: (last) => {
        const next = last.page.offset + last.page.count
        return next < last.page.total ? next : undefined
      },
      refetchInterval: (query) => mergedListPollMs(query.state.data),
    }),
  )
  useRefreshReviewsWhenSettled(mergedListPollMs(query.data) !== false)
  return query
}

/** Super admin: undo a finished merge. */
export function useRestoreMergedPatient() {
  const orpc = useOrpc()
  const queryClient = useQueryClient()
  return useMutation(
    orpc.patientMergeRestore.mutationOptions({
      onSuccess: async (merge) => {
        queryClient.setQueryData(mergeKeys.detail(merge.mergeId), merge)
        await queryClient.invalidateQueries({ queryKey: mergeKeys.merged() })
      },
      onError: (error) =>
        notifyFailure(
          'Restore merged patient',
          error,
          refusalMessage(error) ?? 'Could not restore the record. Please try again.',
        ),
    }),
  )
}

/** Super admin: days a merged record is kept before it is deleted. */
export function useMergeSettings() {
  const orpc = useOrpc()
  return useQuery(orpc.patientMergeSettingsGet.queryOptions({ queryKey: mergeKeys.settings() }))
}

export function useSaveMergeSettings() {
  const orpc = useOrpc()
  const queryClient = useQueryClient()
  return useMutation(
    orpc.patientMergeSettingsSet.mutationOptions({
      onSuccess: async (settings) => {
        queryClient.setQueryData(mergeKeys.settings(), settings)
        showNotification({ color: 'green', message: 'Retention saved.' })
        // Each merged record's "Deletes on" date is derived from the retention.
        await queryClient.invalidateQueries({ queryKey: mergeKeys.merged() })
      },
      onError: (error) =>
        notifyFailure(
          'Save merge settings',
          error,
          'Could not save the retention. Please try again.',
        ),
    }),
  )
}
